import React, { useEffect, useState, useContext, useRef } from "react";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Flex, Image } from "theme-ui";
import { useNavigate } from "react-router-dom";
import { getPurchaseOrders } from "api/PurchaseOrder";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";
import { getPendingProviderInvoices, getMyProviderInvoices } from "api/ProviderInvoice";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";

const PurchaseOrders = () => {
  const [currentQuery, setCurrentQuery] = useState<any>(null);
  const hasTrackedView = useRef(false);
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const { user } = useContext<any>(UserContext);
  const [isProvider, setIsProvider] = useState<boolean>(false);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [pendingPoIds, setPendingPoIds] = useState<Set<string>>(new Set());
  const [rejectedPoIds, setRejectedPoIds] = useState<Set<string>>(new Set());


  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | PO's";

    if (user.provider && user.provider.id) {
      setIsProvider(true);
    } else {
      setIsProvider(false);
    }

    // Fetch pending invoice count for company users
    if (user?.accessLevel === "isCompany") {
      getPendingProviderInvoices().then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPendingCount(res.data.length);
          setPendingPoIds(new Set(res.data.map((inv: any) => inv.purchaseOrderId).filter(Boolean)));
        }
      }).catch(() => { /* non-critical */ });
    }

    // Fetch rejected invoices for provider users
    if (user?.provider?.id) {
      getMyProviderInvoices().then((res) => {
        if (res.success && Array.isArray(res.data)) {
          const rejected = res.data.filter((inv: any) => inv.status === "rejected");
          setRejectedPoIds(new Set(rejected.map((inv: any) => inv.purchaseOrderId).filter(Boolean)));
        }
      }).catch(() => { /* non-critical */ });
    }
  }, []);

  useEffect(() => {
    if (user.provider && user.provider.id) {
      setIsProvider(true);
    } else {
      setIsProvider(false);
    }
  }, [user]);
  useEffect(() => {
    if (!user) return;
    if (!currentQuery) return;
    if (hasTrackedView.current) return;

    hasTrackedView.current = true;

    void trackActivity({
      module: "purchase_orders",
      action: "view_purchase_order_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user, currentQuery]);

  const columns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => {
        const row = info.row.original;
        const poId = row.id;
        const poStatus = Number(row.status);
        const hasPending = pendingPoIds.has(poId);
        const hasRejected = rejectedPoIds.has(poId);

        // For providers: show dot for new/sent POs (needs review), accepted POs without invoice (needs upload), or rejected invoices
        // For company: show dot for pending or rejected provider invoices
        let showDot = false;
        let dotColor = "";

        if (isProvider) {
          if (hasRejected) {
            showDot = true;
            dotColor = "#DC3545"; // Red — rejected invoice needs resubmission
          } else if (poStatus === 1) {
            showDot = true;
            dotColor = "#3B82F6"; // Blue — new PO needs review/acceptance
          }
        } else {
          const poPaymentStatus = Number(row.paymentStatus);
          const poClosedOrPaid = poStatus === 5 || poPaymentStatus === 2;
          if (!poClosedOrPaid) {
            showDot = hasPending || hasRejected;
            dotColor = hasPending ? "#F59E0B" : "#DC3545";
          }
        }

        return (
          <Flex sx={{ alignItems: "center", gap: "8px" }}>
            {showDot && (
              <Box sx={{ width: "8px", height: "8px", minWidth: "8px", borderRadius: "50%", backgroundColor: dotColor }} />
            )}
            <Box sx={{ fontWeight: 600, cursor: "pointer", color: "#000" }} onClick={() => navigate(`/purchase-orders/${poId}`)}>
              {`GMO-${String(info.getValue()).padStart(5, "0")}`}
            </Box>
          </Flex>
        );
      },
      header: () => <span>PO #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.project?.projectName || "Instant Estimate", {
      id: "project.projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Project Name</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="info">
                Sent
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="success">
                Accepted
              </Badge>
            );
          case 3:
            return (
              <Badge as="span" variant="warning">
                Billed
              </Badge>
            );
          case 4:
            return (
              <Badge as="span" variant="danger">
                Denied
              </Badge>
            );
          case 5:
            return (
              <Badge as="span" variant="success">
                Closed
              </Badge>
            );
          case 6:
            return (
              <Badge as="span" variant="danger">
                Cancelled
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
        }
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.provider.name, {
      id: "provider.name",
      cell: (info: any) => info.getValue(),
      header: () => <span>Provider</span>,
    }),
    columnHelper.accessor((row: any) => row?.poTotal, {
      id: "total",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.black }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00"}</Box>;
      },
      header: () => <span>Total</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.paymentStatus, {
      id: "paymentStatus",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="orange">
                Open
              </Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="info">
                Due
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="success">
                Paid
              </Badge>
            );
          case 3:
            return (
              <Badge as="span" variant="success">
                Partial Paid
              </Badge>
            );
          case 4:
            return (
              <Badge as="span" variant="danger">
                Past Due
              </Badge>
            );
          case 5:
            return (
              <Badge as="span" variant="danger">
                Cancelled
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="orange">
                Open
              </Badge>
            );
        }
      },
      header: () => <span>Payment Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>PO Date</span>,
    }),
  ];

  return (
    <>
      <Header></Header>
      <Page title="Purchase Orders" subtitle={`Manage PO's for your company.`}>
        {pendingCount > 0 && user?.accessLevel === "isCompany" && (
          <Box
            sx={{
              backgroundColor: "#FFF3CD",
              border: "1px solid #FFEEBA",
              borderRadius: "6px",
              padding: "12px 16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginBottom="0px" marginTop="0px" color="#856404">
              {pendingCount === 1
                ? "1 provider invoice awaiting review"
                : `${pendingCount} provider invoices awaiting review`}
            </P>
            <BasicButton
              scheme="secondaryOutline"
              onClick={() => navigate("/provider-invoice-review")}
              styles={{ padding: "4px 12px", fontSize: "12px" }}
            >
              Review All
            </BasicButton>
          </Box>
        )}
        <TableData
          api={getPurchaseOrders}
          columns={columns}
          refresh={refreshData}
          title={"All PO's"}
          defaultSortBy="sequence"
          defaultSortDir="desc"
          nothingFoundText="No Purchase Orders found."
          returnQuery={setCurrentQuery}
        />
      </Page>
    </>
  );
};

export default PurchaseOrders;
